import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { PromoGiftRule } from "@jti/commerce";
import { PromoGifts } from "@jti/commerce";
import { toast } from "@jti/core-components/components/Toaster";
import type { AvailableFreeGiftV2 } from "@jti/store/slices/magentoApiSlice";
import {
  useAddPromoGiftToCartMutation,
  useGetCustomerCartIdQuery,
  useGetPromoGiftInfoQuery,
  useRemoveItemFromCartMutation,
} from "@jti/store/slices/magentoApiSlice";
import { useTranslations } from "@jti/ui";

import { useAvailableFreeGifts } from "@checkout-v2/shared/hooks/useAvailableFreeGifts";
import { useCartItems } from "@checkout-v2/shared/hooks/useCartItems";

export const CheckoutPromoGiftsContainer: FC = () => {
  const { __ } = useTranslations();

  const { data: cartIdData } = useGetCustomerCartIdQuery();
  const cartId = cartIdData?.customerCart?.id ?? "";

  const { data: availableFreeGifts } = useAvailableFreeGifts();
  const { data: cartItems } = useCartItems();

  const giftSkus = useMemo(() => {
    if (!availableFreeGifts || availableFreeGifts.length === 0) return [];
    return availableFreeGifts.flatMap((rule) => rule.allowed_gift_products.map((p) => p.sku));
  }, [availableFreeGifts]);

  const { data: promoGiftInfo } = useGetPromoGiftInfoQuery({ skus: giftSkus }, { skip: giftSkus.length === 0 });

  const [removeItemFromCart] = useRemoveItemFromCartMutation();
  const [addPromoGiftToCart] = useAddPromoGiftToCartMutation();

  const [pendingSkus, setPendingSkus] = useState<Record<string, boolean>>({});

  const [hadGiftsOnMount, setHadGiftsOnMount] = useState<boolean | null>(null);

  useEffect(() => {
    if (hadGiftsOnMount === null && cartItems.length > 0 && availableFreeGifts.length > 0) {
      const promoGiftSkuSet = new Set(giftSkus);
      setHadGiftsOnMount(
        cartItems.some((item) => item.isGift && item.isGiftRemovable && promoGiftSkuSet.has(item.sku))
      );
    }
  }, [cartItems, availableFreeGifts, giftSkus, hadGiftsOnMount]);

  const giftInfoMap = useMemo(() => {
    if (!promoGiftInfo) return {};
    return Object.fromEntries(promoGiftInfo.map((info) => [info.sku, info]));
  }, [promoGiftInfo]);

  const getPromoGiftInCart = useCallback(
    (sku: string) => cartItems.find((item) => item.sku === sku && item.isGift && item.isGiftRemovable),
    [cartItems]
  );

  const getSelectedCountForRule = useCallback(
    (rule: AvailableFreeGiftV2): number =>
      rule.allowed_gift_products.reduce((sum, product) => {
        const cartItem = getPromoGiftInCart(product.sku);
        return sum + (cartItem?.quantity || 0);
      }, 0),
    [getPromoGiftInCart]
  );

  const handleQuantityChange = async (salesRuleId: number, sku: string, upcomingValue: number) => {
    const rule = availableFreeGifts.find((r) => r.sales_rule_id === salesRuleId);
    if (!rule) return;

    const product = rule.allowed_gift_products.find((p) => p.sku === sku);
    if (!product) return;

    const cartItem = getPromoGiftInCart(sku);
    const currentValue = cartItem?.quantity || 0;

    if (upcomingValue === currentValue) return;

    const key = `${salesRuleId}_${sku}`;
    setPendingSkus((prev) => ({ ...prev, [key]: true }));
    try {
      if (cartItem) {
        await removeItemFromCart({ cartId, itemId: Number(cartItem.id) }).unwrap();
      }

      if (upcomingValue > 0) {
        const info = giftInfoMap[sku];

        const selectedOptions = info?.selectedOption ? [info.selectedOption] : undefined;
        await addPromoGiftToCart({
          cartId,
          items: [{ sku, quantity: upcomingValue, salesRuleId, selectedOptions }],
        }).unwrap();
      }
    } catch (err) {
      toast.error(__("cart.checkout.free_gifts.error"));
      console.error("Error updating promo gift quantity:", err);
    } finally {
      setPendingSkus((prev) => ({ ...prev, [key]: false }));
    }
  };

  const rules: PromoGiftRule[] = useMemo(() => {
    if (!availableFreeGifts || availableFreeGifts.length === 0) return [];

    return availableFreeGifts.map((rule) => ({
      salesRuleId: rule.sales_rule_id,
      giftQtyLimit: rule.gift_qty_limit,
      selectedCount: getSelectedCountForRule(rule),
      products: rule.allowed_gift_products.map((product) => ({
        sku: product.sku,
        name: product.name,
        imageUrl: giftInfoMap[product.sku]?.imageUrl,
        quantity: getPromoGiftInCart(product.sku)?.quantity || 0,
        pending: !!pendingSkus[`${rule.sales_rule_id}_${product.sku}`],
      })),
    }));
  }, [availableFreeGifts, giftInfoMap, pendingSkus, getPromoGiftInCart, getSelectedCountForRule]);

  if (hadGiftsOnMount || rules.length === 0) return null;

  return <PromoGifts rules={rules} onQuantityChange={handleQuantityChange} />;
};
